import { __awaiter } from "tslib";
import { Connection, PublicKey, clusterApiUrl } from "@solana/web3.js";
import { getAddress } from "@ethersproject/address";
import * as R from "ramda";
import { ReadOnlyRegistry } from "@civic/did-registry";
import { defaultResolver } from "../types";
import { fetchSNSPublicKeyForDomain } from "./fetchSNSPublicKeyForDomain";
import { fetchENSAddressForDomain } from "./fetchENSAddressForDomain";
const asSolanaAddress = (address) => {
    try {
        return new PublicKey(address);
    }
    catch (error) {
        return null;
    }
};
const isEthereumAddress = (address) => !!address.match(/^0x[0-9a-fA-F]{40}$/);
const lookupDidsForAddress = (address, connection) => {
    const normalizedConnection = connection || new Connection(clusterApiUrl("mainnet-beta"));
    return ReadOnlyRegistry.for(address, normalizedConnection).listDIDs();
};
const lookupDidsForEthAddress = (ethAddress, connection) => {
    const normalizedConnection = connection || new Connection(clusterApiUrl("mainnet-beta"));
    return ReadOnlyRegistry.forEthAddress(ethAddress, normalizedConnection).listDIDs();
};
function resolveDid(did, address, resolver = defaultResolver) {
    return __awaiter(this, void 0, void 0, function* () {
        const { didDocument } = yield resolver.resolve(did);
        if (!didDocument) {
            throw new Error(`No DID document found for ${did}`);
        }
        const normalizedAddress = address || didDocument.id.replace(/^did:sol.*:/, "");
        return {
            address: normalizedAddress,
            document: didDocument,
        };
    });
}
/**
 * Given a query, find the array of DIDs that match it.
 * Current query strategies:
 * 1. If the query is a valid DID, return it
 * 2. If the query is a valid Solana address, return the DID directly derived from it,
 * and any DIDs that have been registered against it in the did-registry
 * 3. If the query is a valid Ethereum address, return any DIDs that have been registered
 * against it in the did-registry
 * 4. If the query is a valid SNS domain, return the DID directly derived
 * from the address it is registered against.
 * @param query
 * @param options
 */
const convertQueryToDids = (query, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e;
    // TODO: later, support controller relationships
    if (query.match(/^did:/))
        return [query];
    const solanaAddress = asSolanaAddress(query);
    if (solanaAddress) {
        const directDid = `did:sol:${query}`;
        const registeredDids = yield lookupDidsForAddress(solanaAddress, (_a = options.solana) === null || _a === void 0 ? void 0 : _a.connection);
        return [directDid, ...registeredDids];
    }
    // Reverse lookup for Ethereum addresses against SOL DID
    if (isEthereumAddress(query)) {
        return lookupDidsForEthAddress(query, (_b = options.solana) === null || _b === void 0 ? void 0 : _b.connection);
    }
    // Try ENS (requires .eth suffix)
    if (query.endsWith(".eth")) {
        const addressForENSName = yield fetchENSAddressForDomain(query, (_c = options.ethereum) === null || _c === void 0 ? void 0 : _c.provider);
        if (addressForENSName) {
            return lookupDidsForEthAddress(addressForENSName, (_d = options.solana) === null || _d === void 0 ? void 0 : _d.connection);
        }
    }
    // Try SNS (works even without .sol suffix)
    try {
        const publicKeyForSNSDomainHandle = yield fetchSNSPublicKeyForDomain(query, (_e = options.solana) === null || _e === void 0 ? void 0 : _e.connection);
        const did = `did:sol:${publicKeyForSNSDomainHandle}`;
        return convertQueryToDids(did, options);
    }
    catch (error) {
        if (error.message === "Invalid name account provided") {
            throw new Error(`Domain not found: ${query}`);
        }
        throw error;
    }
});
/**
 * Given a query, find the array of DIDs that match it, and resolve them.
 * At present, resolve only the first DID found.
 * In future, we should resolve them all and return an array of results
 * @param query
 * @param options
 */
function resolveRequestToDid(query, options) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (R.isEmpty(query)) {
            throw new Error("Invalid query");
        }
        try {
            const dids = yield convertQueryToDids(query, options);
            // if the query was a solana address, use that, otherwise get the address from the DID
            // TODO consider removing
            const address = ((_a = asSolanaAddress(query)) === null || _a === void 0 ? void 0 : _a.toBase58()) || null;
            if (dids.length > 0) {
                // resolve the top DID for now. TODO: support multiple DIDs
                return resolveDid(dids[0], address, options.resolver);
            }
            // If the query is an ethereum address, return a DID for it
            if (query.match(/^(0x)/g)) {
                // Convert non-checksummed address into a checksummed address
                const did = `did:pkh:eip155:1:${getAddress(query)}`;
                return resolveDid(did, address, options.resolver);
            }
            // No DIDs found, so try resolving the query directly
            return resolveDid(query, address, options.resolver);
        }
        catch (error) {
            throw new Error(`Unable to resolve [${query}] to did: ${error === null || error === void 0 ? void 0 : error.message}`);
        }
    });
}
export default {
    resolveRequestToDid,
    convertQueryToDids,
};
